import produce from 'immer';
import { push } from 'react-router-redux';
import type { RemoteData } from '../async-actions';
import { createFetchAction, genericErrorToast } from '../async-actions';
import { User } from '../helpers';
import { toastr } from '../helpers/Toastr';
import * as Hub from '../ogm-hub';

// ======
// State
// ======

export enum CurrentStep {
	'InputProducts' = 1,
	'VerifyProducts',
	'SelectOrderGuides',
	'ReviewChanges',
	'CommitChanges',
}

export interface MassChangeState {
	selectOrderGuideViewModel: RemoteData<
		SelectOrderGuidePageViewModel,
		string[]
	>;
	reviewChangesViewModel: RemoteData<ReviewChangesPageViewModel, string[]>;
	verifyProductsViewModel: RemoteData<VerifyProductsPageViewModel, string[]>;
	dataModel: MassChangeDataModel;
	productInputViewModel: RemoteData<ProductInputViewModel, string[]>;
}

export interface SelectOrderGuidePageViewModel {
	selectFilterInput: string;
	unselectFilterInput: string;
	filteredUnselectedOrderGuides: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	filteredSelectedOrderGuides: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	addOrDelete: Hub.MassChangeRequestType;
	isNextButtonDisabled: boolean;
	isMultiSelectButtonDisabled: boolean;
	isMultiUnselectButtonDisabled: boolean;
}

export interface ReviewChangesPageViewModel {
	isStaged: boolean;
	addOrDelete: Hub.MassChangeRequestType;
	reviewOrderGuides: ReviewChangesMOGData[];
	blueprints: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	suppProdNums: string[];
}

export interface ProductData {
	lineId: number;
	productNumber: string;
	description: string;
	unitOfMeasure: string;
	manufacturerName: string;
	hasError: boolean;
	isActive: boolean;
}

export interface VerifyProductsPageViewModel {
	productData: ProductData[];
	supplierName: string;
	suppProdNums: string[];
	isNextButtonDisabled: boolean;
}

export interface ProductInputViewModel {
	availableSuppliers: Hub.GetProductInputPageDataResult_Supplier[];
	selectedSupplier: Hub.GetProductInputPageDataResult_Supplier;
	input: string;
	addRemove: Hub.MassChangeRequestType;
	verifyDisabled: boolean;
}

export interface MassChangeDataModel {
	source: 'new' | 'continue';
	selectFilterInput: string;
	unselectFilterInput: string;
	allOrderGuides: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	selectedOrderGuides: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	supplier: Hub.GetProductInputPageDataResult_Supplier;
	suppProdNums: string[];
	addOrDelete: Hub.MassChangeRequestType;
	currentStep: CurrentStep;
	reviewChangesOrderGuides: ReviewChangesMOGData[];
	productData: ProductData[];
	productNumbers: string[];
	suppliers: Hub.GetProductInputPageDataResult_Supplier[];
	input: string;
	massChangeRequestId: number;
	isStaged: boolean;
}

export interface ReviewChangesMOGData {
	name: string;
	addRemoveCount: number;
	noActionCount: number;
}

export const initialDataModel: MassChangeDataModel = {
	source: 'new',
	selectFilterInput: '',
	unselectFilterInput: '',
	suppProdNums: [],
	allOrderGuides: [],
	selectedOrderGuides: [],
	addOrDelete: 1,
	currentStep: 1,
	supplier: { id: null, corpSupplierCode: null, name: null },
	suppliers: [],
	productNumbers: [],
	input: '',
	reviewChangesOrderGuides: [],
	productData: [],
	massChangeRequestId: -1,
	isStaged: false,
};

export function initialState(): MassChangeState {
	return {
		selectOrderGuideViewModel: { type: 'initial' },
		productInputViewModel: { type: 'initial' },
		reviewChangesViewModel: { type: 'initial' },
		verifyProductsViewModel: { type: 'initial' },
		dataModel: initialDataModel,
	};
}

export type ColumnType = 'select' | 'deselect';

// ========
// Reducer
// ========
export function MassChangeReducer(
	state = initialState(),
	action: MassChangeAction
): MassChangeState {
	return produce(state, (next) => {
		switch (action.type) {
			case '@mass-change/SELECT_ORDER_GUIDE_SEARCH_BOX_UPDATE': {
				const { value, columnType } = action.payload;

				switch (columnType) {
					case 'select': {
						next.dataModel.selectFilterInput = value;
						break;
					}
					case 'deselect': {
						next.dataModel.unselectFilterInput = value;
						break;
					}
				}
				break;
			}
			case '@mass-change/SELECT_ORDER_GUIDE_SELECTION': {
				const { selectedOrderGuide } = action.payload;
				next.dataModel.selectedOrderGuides.push(selectedOrderGuide);
				break;
			}
			case '@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_PENDING': {
				next.dataModel.currentStep = state.dataModel.currentStep + 1;

				if (next.productInputViewModel.type !== 'success') {
					next.productInputViewModel = { type: 'loading' };
				}
				if (next.reviewChangesViewModel.type !== 'success') {
					next.reviewChangesViewModel = { type: 'loading' };
				}
				if (next.selectOrderGuideViewModel.type !== 'success') {
					next.selectOrderGuideViewModel = { type: 'loading' };
				}
				if (next.verifyProductsViewModel.type !== 'success') {
					next.verifyProductsViewModel = { type: 'loading' };
				}
				break;
			}
			case '@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_SUCCESS': {
				if (action.payload.target === 'verify-products') {
					next.verifyProductsViewModel.type = 'success';
					next.dataModel.productData = action.payload.res.items.map((x, i) => ({
						productNumber: x.supplierProductNumber,
						hasError: !x.isValid,
						lineId: i,
						unitOfMeasure: x.unitsOfMeasure.join(', '),
						description: x.itemDescription,
						manufacturerName: x.brand,
						isActive: x.isActive,
					}));
				}
				if (action.payload.target === 'select-mogs') {
					next.selectOrderGuideViewModel.type = 'success';
					next.dataModel.allOrderGuides = action.payload.res.masterOrderGuides;
				}
				if (action.payload.target === 'review-changes') {
					next.reviewChangesViewModel.type = 'success';
					next.dataModel.massChangeRequestId =
						action.payload.res.massChangeRequestId;
					next.dataModel.isStaged = false;
				}

				break;
			}
			case '@mass-change/REQUEST_STATUS_POLLED_SUCCESS': {
				if (action.payload.isStaged) {
					next.dataModel.isStaged = true;
					next.dataModel.addOrDelete = action.payload.addOrDelete;
					next.dataModel.reviewChangesOrderGuides =
						action.payload.blueprints.map((x) => {
							return {
								name: x.name,
								addRemoveCount: x.changeCount,
								noActionCount: x.noActionCount,
							};
						});
				}

				break;
			}
			case '@mass-change/MASS_CHANGE_PREV_BUTTON_CLICKED': {
				next.dataModel.currentStep = state.dataModel.currentStep - 1;
				break;
			}
			case '@mass-change/SELECT_ORDER_GUIDE_DESELECTION': {
				const { deselectedOrderGuide } = action.payload;
				removeElementFromArray(
					next.dataModel.selectedOrderGuides,
					deselectedOrderGuide
				);
				break;
			}
			case '@mass-change/SELECT_ORDER_GUIDE_MULTI_SELECT_BUTTON_CLICKED': {
				const { columnType } = action.payload;
				switch (columnType) {
					case 'select': {
						const filteredUnselectedOrderGuides = next.dataModel.allOrderGuides
							.filter(
								(b) =>
									!next.dataModel.selectedOrderGuides
										.map((b2) => b2.id)
										.includes(b.id)
							)
							.filter((b) => b.name.includes(next.dataModel.selectFilterInput));
						filteredUnselectedOrderGuides.forEach((og) =>
							next.dataModel.selectedOrderGuides.push(og)
						);
						break;
					}
					case 'deselect': {
						const filteredSelectOrderGuides =
							next.dataModel.selectedOrderGuides.filter((b) =>
								b.name.includes(next.dataModel.unselectFilterInput)
							);
						filteredSelectOrderGuides.forEach((og) =>
							removeElementFromArray(next.dataModel.selectedOrderGuides, og)
						);
						break;
					}
				}
				break;
			}
			case '@mass-change/PRODUCT_INPUT_ACTION_SELECTED': {
				const { addOrDelete } = action.payload;
				next.dataModel.addOrDelete = addOrDelete;
				break;
			}
			case '@mass-change/PRODUCT_INPUT_SUPPLIER_SELECTED': {
				const { selectedSupplierCode } = action.payload;

				if (!selectedSupplierCode) {
					next.dataModel.supplier = {
						corpSupplierCode: null,
						id: null,
						name: null,
					};
				} else {
					next.dataModel.supplier = next.dataModel.suppliers.find(
						(supplier) => supplier.corpSupplierCode === selectedSupplierCode
					);
				}

				break;
			}
			case '@mass-change/MASS_CHANGE_PAGE_LOADED_SUCCESS': {
				if (action.payload.type === 'new') {
					next.dataModel.source = 'new';
					next.dataModel.suppliers = action.payload.data.suppliers;
					next.productInputViewModel.type = 'success';
				}
				if (action.payload.type === 'continue') {
					next.dataModel.source = 'continue';
					next.dataModel.currentStep = CurrentStep.ReviewChanges;
					next.dataModel.isStaged = true;
					next.reviewChangesViewModel.type = 'success';
					next.dataModel.addOrDelete = action.payload.data.addOrDelete;
					next.dataModel.reviewChangesOrderGuides =
						action.payload.data.blueprints.map((x) => {
							return {
								name: x.name,
								addRemoveCount: x.changeCount,
								noActionCount: x.noActionCount,
							};
						});
				}
				break;
			}
			case '@mass-change/PRODUCT_INPUT_PRODUCT_NUMBERS_CHANGED': {
				const { input } = action.payload;
				const formattedInput = input.split(',').join('\n');
				next.dataModel.input = formattedInput;
				next.dataModel.productNumbers = formattedInput
					.trim()
					.replace('\r\n', '\n')
					.split('\n')
					.map((x) => x.trim())
					.filter((x) => x !== '');
				break;
			}
			case '@mass-change/MASS_CHANGE_PAGE_LOADED_PENDING': {
				const newState = initialState();
				const { massChangeRequestId } = action.payload;
				if (massChangeRequestId) {
					newState.dataModel.massChangeRequestId = massChangeRequestId;
				}
				return newState;
			}
			case '@mass-change/VERIFY_PRODUCTS_LINE_DELETED': {
				const { id } = action.payload;
				const index = next.dataModel.productData.findIndex(
					(product) => product.lineId === id
				);
				if (index > -1) {
					next.dataModel.productData.splice(index, 1);
				}
				break;
			}
			case '@mass-change/VERIFY_PRODUCTS_PRODUCT_NUMBER_UPDATED': {
				const { id, value } = action.payload;
				const index = next.dataModel.productData.findIndex(
					(product) => product.lineId === id
				);
				if (index > -1) {
					next.dataModel.productData[index].productNumber = value;
				} else {
					next.dataModel.productData.push({
						productNumber: value,
						description: undefined,
						hasError: false,
						lineId: id,
						manufacturerName: undefined,
						unitOfMeasure: undefined,
						isActive: false,
					});
				}

				break;
			}
			case '@mass-change/CHANGES_APPLIED_SUCCESS': {
				next.dataModel.currentStep += 1;
				break;
			}
			case '@mass-change/ANOTHER_MASS_CHANGE_STARTED_SUCCESS': {
				const newState = initialState();
				newState.dataModel.suppliers = action.payload.suppliers;
				newState.dataModel.source = 'new';
				newState.productInputViewModel = {
					type: 'success',
					data: calculateProductInputChangesViewFromData(newState.dataModel),
				};
				return newState;
			}
		}
		// TODO BEFORE PHASE2: calculate loading status
		if (next.productInputViewModel.type === 'success') {
			next.productInputViewModel.data =
				calculateProductInputChangesViewFromData(next.dataModel);
		}

		if (next.selectOrderGuideViewModel.type === 'success') {
			next.selectOrderGuideViewModel.data =
				calculateSelectOrderGuideViewFromData(next.dataModel);
		}

		if (next.reviewChangesViewModel.type === 'success') {
			next.reviewChangesViewModel.data = calculateReviewChangesViewFromData(
				next.dataModel
			);
		}

		if (next.verifyProductsViewModel.type === 'success') {
			next.verifyProductsViewModel.data = calculateVerifyProductsViewFromData(
				next.dataModel
			);
		}
	});
}

// ======
// Reducer Helpers
// ======

// todo make this generic if needed

function removeElementFromArray(
	array: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[],
	element: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide
) {
	const index = array.findIndex((s) => s.id === element.id);
	array.splice(index, 1);
}

export function calculateSelectOrderGuideViewFromData(
	dataModel: MassChangeDataModel
): SelectOrderGuidePageViewModel {
	const filteredSelectedOrderGuides = dataModel.selectedOrderGuides.filter(
		(b) =>
			b.name.toLowerCase().includes(dataModel.unselectFilterInput.toLowerCase())
	);
	const filteredUnselectedOrderGuides = dataModel.allOrderGuides
		.filter(
			(b) => !dataModel.selectedOrderGuides.map((b2) => b2.id).includes(b.id)
		)
		.filter((b) =>
			b.name.toLowerCase().includes(dataModel.selectFilterInput.toLowerCase())
		);
	return {
		filteredSelectedOrderGuides,
		filteredUnselectedOrderGuides,
		selectFilterInput: dataModel.selectFilterInput,
		unselectFilterInput: dataModel.unselectFilterInput,
		addOrDelete: dataModel.addOrDelete,
		isMultiSelectButtonDisabled: filteredUnselectedOrderGuides.length === 0,
		isMultiUnselectButtonDisabled: filteredSelectedOrderGuides.length === 0,
		isNextButtonDisabled: dataModel.selectedOrderGuides.length === 0,
	};
}

function calculateReviewChangesViewFromData(
	dataModel: MassChangeDataModel
): ReviewChangesPageViewModel {
	return {
		isStaged: dataModel.isStaged,
		reviewOrderGuides: dataModel.reviewChangesOrderGuides,
		addOrDelete: dataModel.addOrDelete,
		blueprints: dataModel.selectedOrderGuides,
		suppProdNums: dataModel.productData
			.filter((product) => !product.hasError)
			.map((product) => product.productNumber),
	};
}

function calculateVerifyProductsViewFromData(
	dataModel: MassChangeDataModel
): VerifyProductsPageViewModel {
	return {
		productData: dataModel.productData,
		supplierName: dataModel.supplier.name,
		suppProdNums: dataModel.productNumbers,
		isNextButtonDisabled:
			dataModel.productData.filter((x) => !x.hasError).length === 0,
	};
}

function calculateProductInputChangesViewFromData(
	dataModel: MassChangeDataModel
): ProductInputViewModel {
	return {
		availableSuppliers: dataModel.suppliers,
		selectedSupplier: dataModel.supplier,
		input: dataModel.input,
		addRemove: dataModel.addOrDelete,
		verifyDisabled:
			dataModel.input.length <= 0 ||
			dataModel.supplier.corpSupplierCode === null,
	};
}

// ========
// Actions
// ========

export type MassChangeAction =
	| SELECT_ORDER_GUIDE_SELECTION
	| SELECT_ORDER_GUIDE_SEARCH_BOX_UPDATE
	| MASS_CHANGE_NEXT_BUTTON_CLICKED_PENDING
	| MASS_CHANGE_NEXT_BUTTON_CLICKED_FAILURE
	| MASS_CHANGE_NEXT_BUTTON_CLICKED_SUCCESS
	| MASS_CHANGE_PREV_BUTTON_CLICKED
	| VERIFY_PRODUCTS_LINE_DELETED
	| VERIFY_PRODUCTS_PRODUCT_NUMBER_UPDATED
	| SELECT_ORDER_GUIDE_DESELECTION
	| SELECT_ORDER_GUIDE_MULTI_SELECT_BUTTON_CLICKED
	| PRODUCT_INPUT_ACTION_SELECTED
	| PRODUCT_INPUT_SUPPLIER_SELECTED
	| MASS_CHANGE_PAGE_LOADED_PENDING
	| MASS_CHANGE_PAGE_LOADED_SUCCESS
	| PRODUCT_INPUT_PRODUCT_NUMBERS_CHANGED
	| REQUEST_STATUS_POLLED_SUCCESS
	| CHANGES_DISCARDED_SUCCESS
	| CHANGES_APPLIED_SUCCESS
	| ANOTHER_MASS_CHANGE_STARTED_SUCCESS;

export interface VerifyProductsPageLoadedArgs {
	suppProdNums: string[];
}

export interface ReviewChangesPageLoadedArgs {
	blueprints: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	suppProdNums: string[];
}

export interface SelectOrderGuidePageSelectionArgs {
	selectedOrderGuide: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide;
}

export interface SELECT_ORDER_GUIDE_SELECTION {
	type: '@mass-change/SELECT_ORDER_GUIDE_SELECTION';
	payload: SelectOrderGuidePageSelectionArgs;
}

export function selectOrderGuidePageSelected(
	selectedOrderGuide: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide
): SELECT_ORDER_GUIDE_SELECTION {
	return {
		type: '@mass-change/SELECT_ORDER_GUIDE_SELECTION',
		payload: {
			selectedOrderGuide,
		},
	};
}

export interface SelectionOrderGuideDeselectionArgs {
	deselectedOrderGuide: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide;
}

export interface SELECT_ORDER_GUIDE_DESELECTION {
	type: '@mass-change/SELECT_ORDER_GUIDE_DESELECTION';
	payload: SelectionOrderGuideDeselectionArgs;
}

export function selectOrderGuidePageDeselected(
	deselectedOrderGuide: Hub.SelectMasterOrderGuidesDataResult_MasterOrderGuide
): SELECT_ORDER_GUIDE_DESELECTION {
	return {
		type: '@mass-change/SELECT_ORDER_GUIDE_DESELECTION',
		payload: {
			deselectedOrderGuide,
		},
	};
}

export interface SelectOrderGuidePageSearchBoxUpdateArgs {
	value: string;
	columnType: ColumnType;
}

export interface SELECT_ORDER_GUIDE_SEARCH_BOX_UPDATE {
	type: '@mass-change/SELECT_ORDER_GUIDE_SEARCH_BOX_UPDATE';
	payload: SelectOrderGuidePageSearchBoxUpdateArgs;
}

export function selectOrderGuidePageSearchBoxUpdate(
	value: string,
	columnType: ColumnType
): SELECT_ORDER_GUIDE_SEARCH_BOX_UPDATE {
	return {
		type: '@mass-change/SELECT_ORDER_GUIDE_SEARCH_BOX_UPDATE',
		payload: {
			value,
			columnType,
		},
	};
}

export interface SelectOrderGuidePageMultiSelectButtonArgs {
	columnType: ColumnType;
}

export interface SELECT_ORDER_GUIDE_MULTI_SELECT_BUTTON_CLICKED {
	type: '@mass-change/SELECT_ORDER_GUIDE_MULTI_SELECT_BUTTON_CLICKED';
	payload: SelectOrderGuidePageMultiSelectButtonArgs;
}

export function selectOrderGuidePageMultiSelectButtonClicked(
	columnType: ColumnType
): SELECT_ORDER_GUIDE_MULTI_SELECT_BUTTON_CLICKED {
	return {
		type: '@mass-change/SELECT_ORDER_GUIDE_MULTI_SELECT_BUTTON_CLICKED',
		payload: {
			columnType,
		},
	};
}

export interface MASS_CHANGE_NEXT_BUTTON_CLICKED_PENDING {
	type: '@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_PENDING';
}
export interface MASS_CHANGE_NEXT_BUTTON_CLICKED_FAILURE {
	type: '@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_FAILURE';
	errors: string[];
}
export interface MASS_CHANGE_NEXT_BUTTON_CLICKED_SUCCESS {
	type: '@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_SUCCESS';
	payload:
		| {
				target: 'verify-products';
				res: Hub.VerifyProductsResult;
		  }
		| {
				target: 'select-mogs';
				res: Hub.SelectMasterOrderGuidesDataResult;
		  }
		| {
				target: 'review-changes';
				res: Hub.ReviewChangesResult;
		  }
		| {
				target: 'todo';
		  };
}

export const massChangePageNextButtonClicked = createFetchAction<
	{},
	MASS_CHANGE_NEXT_BUTTON_CLICKED_PENDING,
	MASS_CHANGE_NEXT_BUTTON_CLICKED_FAILURE,
	MASS_CHANGE_NEXT_BUTTON_CLICKED_SUCCESS
>(
	'@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_PENDING',
	'@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_FAILURE',
	'@mass-change/MASS_CHANGE_NEXT_BUTTON_CLICKED_SUCCESS',
	async (args, dispatch, getState) => {
		// TODO BEFORE PH2:
		// This seems like too much logic in one action creator.
		// Might be a better idea to split out into multiple.
		const state = getState().MassChange;
		const currentStep = state.dataModel.currentStep;

		// Step 2 Verify Items
		if (currentStep === CurrentStep.VerifyProducts) {
			const itemNumbers = state.dataModel.productNumbers;
			const supplierId = state.dataModel.supplier.id;
			const res = await Hub.ProductController.VerifyProducts({
				itemNumbers,
				supplierId,
			});
			if (res.kind !== 'success') {
				genericErrorToast(res);
				return res;
			}
			return { target: 'verify-products', res: res.value };
		}

		// Step 3 Select Master Order Guides
		if (currentStep === CurrentStep.SelectOrderGuides) {
			const supplierId = state.dataModel.supplier.id;
			const res = await Hub.ProductController.SelectMasterOrderGuidesData({
				supplierId,
				customerId: null,
			});
			if (res.kind !== 'success') {
				genericErrorToast(res);
				return res;
			}
			return { target: 'select-mogs', res: res.value };
		}

		// Step 4 Review Changes
		if (currentStep === CurrentStep.ReviewChanges) {
			const supplierId = state.dataModel.supplier.id;
			const itemNumbers = state.dataModel.productNumbers;
			const blueprintIds = state.dataModel.selectedOrderGuides.map((x) => x.id);
			const addOrDelete = state.dataModel.addOrDelete;

			const res = await Hub.ProductController.ReviewChanges({
				supplierId,
				itemNumbers,
				blueprintIds,
				addOrDelete,
			});

			if (res.kind !== 'success') {
				genericErrorToast(res);
				return res;
			}

			dispatch(requestStatusPolled());

			return { target: 'review-changes', res: res.value };
		}

		throw new Error('not implemented');
	}
);

export interface REQUEST_STATUS_POLLED_PENDING {
	type: '@mass-change/REQUEST_STATUS_POLLED_PENDING';
}
export interface REQUEST_STATUS_POLLED_FAILURE {
	type: '@mass-change/REQUEST_STATUS_POLLED_FAILURE';
	errors: string[];
}
export interface REQUEST_STATUS_POLLED_SUCCESS {
	type: '@mass-change/REQUEST_STATUS_POLLED_SUCCESS';
	payload: Hub.PollRequestStatusResult;
}

export const requestStatusPolled = createFetchAction<
	void,
	REQUEST_STATUS_POLLED_PENDING,
	REQUEST_STATUS_POLLED_FAILURE,
	REQUEST_STATUS_POLLED_SUCCESS
>(
	'@mass-change/REQUEST_STATUS_POLLED_PENDING',
	'@mass-change/REQUEST_STATUS_POLLED_FAILURE',
	'@mass-change/REQUEST_STATUS_POLLED_SUCCESS',
	async (args, dispatch, getState) => {
		const state = getState().MassChange.dataModel;
		const { massChangeRequestId } = state;

		const res = await Hub.ProductController.PollRequestStatus({
			massChangeRequestId,
		});

		if (res.kind !== 'success') {
			genericErrorToast(res);
			return res;
		}

		// Ask again in a second if it wasn't staged yet.
		if (!res.value.isStaged) {
			window.setTimeout(() => dispatch(requestStatusPolled()), 1000);
		}

		return res;
	}
);

export interface MASS_CHANGE_PREV_BUTTON_CLICKED {
	type: '@mass-change/MASS_CHANGE_PREV_BUTTON_CLICKED';
}

export function massChangePagePrevButtonClicked(): MASS_CHANGE_PREV_BUTTON_CLICKED {
	return {
		type: '@mass-change/MASS_CHANGE_PREV_BUTTON_CLICKED',
	};
}

export interface ProductInputActionSelectedArgs {
	addOrDelete: Hub.MassChangeRequestType;
}

export interface PRODUCT_INPUT_ACTION_SELECTED {
	type: '@mass-change/PRODUCT_INPUT_ACTION_SELECTED';
	payload: ProductInputActionSelectedArgs;
}

export function productInputActionSelected(
	addOrDelete: Hub.MassChangeRequestType
): PRODUCT_INPUT_ACTION_SELECTED {
	return {
		type: '@mass-change/PRODUCT_INPUT_ACTION_SELECTED',
		payload: {
			addOrDelete,
		},
	};
}

export interface ProductInputSupplierSelectedArgs {
	selectedSupplierCode: string;
}

export interface PRODUCT_INPUT_SUPPLIER_SELECTED {
	type: '@mass-change/PRODUCT_INPUT_SUPPLIER_SELECTED';
	payload: ProductInputSupplierSelectedArgs;
}

export function productInputSupplierSelected(
	selectedSupplierCode: string
): PRODUCT_INPUT_SUPPLIER_SELECTED {
	return {
		type: '@mass-change/PRODUCT_INPUT_SUPPLIER_SELECTED',
		payload: {
			selectedSupplierCode,
		},
	};
}

export interface MassChangePageLoadedPendingArgs {
	massChangeRequestId?: number;
}

export interface MASS_CHANGE_PAGE_LOADED_PENDING {
	type: '@mass-change/MASS_CHANGE_PAGE_LOADED_PENDING';
	payload: MassChangePageLoadedPendingArgs;
}

export interface MASS_CHANGE_PAGE_LOADED_FAILURE {
	type: '@mass-change/MASS_CHANGE_PAGE_LOADED_FAILURE';
	errors: string[];
}

export interface MASS_CHANGE_PAGE_LOADED_SUCCESS {
	type: '@mass-change/MASS_CHANGE_PAGE_LOADED_SUCCESS';
	payload:
		| {
				type: 'new';
				data: Hub.GetProductInputPageDataResult;
		  }
		| {
				type: 'continue';
				data: Hub.PollRequestStatusResult;
		  };
}

export interface ProductInputProductNumbersChangedArgs {
	input: string;
}

export interface PRODUCT_INPUT_PRODUCT_NUMBERS_CHANGED {
	type: '@mass-change/PRODUCT_INPUT_PRODUCT_NUMBERS_CHANGED';
	payload: ProductInputProductNumbersChangedArgs;
}

export function productInputProductNumbersChanged(
	input: string
): PRODUCT_INPUT_PRODUCT_NUMBERS_CHANGED {
	return {
		type: '@mass-change/PRODUCT_INPUT_PRODUCT_NUMBERS_CHANGED',
		payload: {
			input,
		},
	};
}

export const massChangePageLoaded = createFetchAction<
	MassChangePageLoadedPendingArgs,
	MASS_CHANGE_PAGE_LOADED_PENDING,
	MASS_CHANGE_PAGE_LOADED_FAILURE,
	MASS_CHANGE_PAGE_LOADED_SUCCESS
>(
	'@mass-change/MASS_CHANGE_PAGE_LOADED_PENDING',
	'@mass-change/MASS_CHANGE_PAGE_LOADED_FAILURE',
	'@mass-change/MASS_CHANGE_PAGE_LOADED_SUCCESS',
	async (args, dispatch) => {
		if (!User.canCreateMassChangeRequests()) {
			dispatch(push('/404'));
			return;
		}

		if (args.massChangeRequestId) {
			const result = await Hub.ProductController.PollRequestStatus({
				massChangeRequestId: args.massChangeRequestId,
			});

			if (result.kind !== 'success') {
				return result;
			}

			return { type: 'continue', data: result.value };
		}

		const res = await Hub.ProductController.GetMassChangePageData();

		if (res.kind !== 'success') {
			return res;
		}

		if (!res.value.canStartNewMassChange) {
			toastr.error({
				title: 'Cannot Start Mass Change',
				body: 'Please discard or apply changes for the current Mass Change.',
			});
			dispatch(push('/mass-change'));
			return;
		}

		return { type: 'new', data: res.value };
	},
	(args) => args
);

export interface ProductNumberUpdatedArgs {
	id: number;
	value: string;
}

export interface VERIFY_PRODUCTS_PRODUCT_NUMBER_UPDATED {
	type: '@mass-change/VERIFY_PRODUCTS_PRODUCT_NUMBER_UPDATED';
	payload: ProductNumberUpdatedArgs;
}

export function productNumberUpdated(id: number, value: string) {
	return {
		type: '@mass-change/VERIFY_PRODUCTS_PRODUCT_NUMBER_UPDATED',
		payload: {
			id,
			value,
		},
	};
}

export interface VerifyProductsLineDeletedArgs {
	id: number;
}

export interface VERIFY_PRODUCTS_LINE_DELETED {
	type: '@mass-change/VERIFY_PRODUCTS_LINE_DELETED';
	payload: VerifyProductsLineDeletedArgs;
}

export function productLineDeleted(id: number) {
	return {
		type: '@mass-change/VERIFY_PRODUCTS_LINE_DELETED',
		payload: {
			id,
		},
	};
}

export interface CHANGES_DISCARDED_PENDING {
	type: '@mass-change/CHANGES_DISCARDED_PENDING';
}
export interface CHANGES_DISCARDED_FAILURE {
	type: '@mass-change/CHANGES_DISCARDED_FAILURE';
	errors: string[];
}
export interface CHANGES_DISCARDED_SUCCESS {
	type: '@mass-change/CHANGES_DISCARDED_SUCCESS';
	payload: Hub.DiscardChangesResult;
}

export const changesDiscarded = createFetchAction<
	void,
	CHANGES_DISCARDED_PENDING,
	CHANGES_DISCARDED_FAILURE,
	CHANGES_DISCARDED_SUCCESS
>(
	'@mass-change/CHANGES_DISCARDED_PENDING',
	'@mass-change/CHANGES_DISCARDED_FAILURE',
	'@mass-change/CHANGES_DISCARDED_SUCCESS',
	async (args, dispatch, getState) => {
		const massChangeRequestId =
			getState().MassChange.dataModel.massChangeRequestId;

		const res = await Hub.ProductController.DiscardChanges({
			massChangeRequestId,
		});

		if (res.kind !== 'success') {
			return res;
		}

		dispatch(push('/mass-change'));
		return res;
	}
);

export interface CHANGES_APPLIED_PENDING {
	type: '@mass-change/CHANGES_APPLIED_PENDING';
}
export interface CHANGES_APPLIED_FAILURE {
	type: '@mass-change/CHANGES_APPLIED_FAILURE';
	errors: string[];
}
export interface CHANGES_APPLIED_SUCCESS {
	type: '@mass-change/CHANGES_APPLIED_SUCCESS';
	payload: Hub.ApplyChangesResult;
}

export const changesApplied = createFetchAction<
	void,
	CHANGES_APPLIED_PENDING,
	CHANGES_APPLIED_FAILURE,
	CHANGES_APPLIED_SUCCESS
>(
	'@mass-change/CHANGES_APPLIED_PENDING',
	'@mass-change/CHANGES_APPLIED_FAILURE',
	'@mass-change/CHANGES_APPLIED_SUCCESS',
	async (args, dispatch, getState) => {
		const massChangeRequestId =
			getState().MassChange.dataModel.massChangeRequestId;

		const res = await Hub.ProductController.ApplyChanges({
			massChangeRequestId,
		});

		return res;
	}
);

export interface ANOTHER_MASS_CHANGE_STARTED_PENDING {
	type: '@mass-change/ANOTHER_MASS_CHANGE_STARTED_PENDING';
}
export interface ANOTHER_MASS_CHANGE_STARTED_FAILURE {
	type: '@mass-change/ANOTHER_MASS_CHANGE_STARTED_FAILURE';
	errors: string[];
}
export interface ANOTHER_MASS_CHANGE_STARTED_SUCCESS {
	type: '@mass-change/ANOTHER_MASS_CHANGE_STARTED_SUCCESS';
	payload: Hub.GetProductInputPageDataResult;
}

export const anotherMassChangeStarted = createFetchAction<
	void,
	ANOTHER_MASS_CHANGE_STARTED_PENDING,
	ANOTHER_MASS_CHANGE_STARTED_FAILURE,
	ANOTHER_MASS_CHANGE_STARTED_SUCCESS
>(
	'@mass-change/ANOTHER_MASS_CHANGE_STARTED_PENDING',
	'@mass-change/ANOTHER_MASS_CHANGE_STARTED_FAILURE',
	'@mass-change/ANOTHER_MASS_CHANGE_STARTED_SUCCESS',
	async (args, dispatch, getState) => {
		const state = getState().MassChange.dataModel;

		const res = await Hub.ProductController.GetMassChangePageData();

		if (res.kind !== 'success') {
			return res;
		}

		if (!res.value.canStartNewMassChange) {
			toastr.error({
				title: 'Cannot Start Mass Change',
				body: 'Please discard or apply changes for the current Mass Change.',
			});
			dispatch(push('/mass-change'));
			return res;
		}

		if (state.source === 'continue') {
			dispatch(push('/mass-change/new'));
		}

		return res;
	}
);
